/**
 * Login — Anmeldeseite mit E-Mail und Passwort.
 *
 * Features:
 * - E-Mail/Passwort-Formular mit Validierung
 * - Automatische Weiterleitung nach Login (Kunden -> Portal, Rest -> Dashboard)
 * - Fehleranzeige bei ungültigen Credentials
 *
 * @page /login (unauthentifiziert)
 * @roles keine (öffentlich)
 * @i18n auth
 * @endpoints POST /api/auth/login (via AuthContext)
 */
import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';

export default function Login() {
  const { login } = useAuth();
  const { t } = useLanguage();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const user = await login(email, password);
      navigate(user.rolle === 'kunde' ? '/portal' : '/', { replace: true });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-surface flex items-center justify-center px-4">
      <div className="w-full max-w-sm">
        {/* Logo */}
        <div className="flex flex-col items-center mb-8">
          <img src={theme === 'dark' ? '/weship-logo-light.png' : '/weship-logo.png'} alt="WeShip" className="h-10 mb-4" />
          <h1 className="text-xl font-bold text-content">3PL Management</h1>
          <p className="text-sm text-[var(--text-secondary)] mt-1">{t('auth.loginSubtitle')}</p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="bg-surface-card border border-line-card rounded-card p-6 space-y-4 shadow-card">
          {error && (
            <div className="bg-red-500/10 border border-red-500/20 rounded-lg px-4 py-2.5 text-sm text-red-400">
              {error}
            </div>
          )}

          <div>
            <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1.5">{t('auth.email')}</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-surface-input border border-line-input rounded-lg px-3 py-2.5 text-sm text-content placeholder-content-dim focus:outline-none focus:border-primary-500/50 focus:ring-1 focus:ring-primary-500/30 transition-colors"
              placeholder="name@weship.eu"
              required
              autoFocus
              autoComplete="email"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1.5">{t('auth.password')}</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full bg-surface-input border border-line-input rounded-lg px-3 py-2.5 text-sm text-content placeholder-content-dim focus:outline-none focus:border-primary-500/50 focus:ring-1 focus:ring-primary-500/30 transition-colors"
              placeholder={t('auth.passwordPlaceholder')}
              required
              autoComplete="current-password"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-[#338edc] hover:bg-[#2b7bc4] disabled:bg-[#338edc]/50 disabled:cursor-not-allowed text-white font-medium text-sm py-2.5 rounded-lg transition-colors"
          >
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" fill="none" strokeDasharray="31.4 31.4" strokeLinecap="round" /></svg>
                {t('auth.loggingIn')}
              </span>
            ) : t('auth.login')}
          </button>
        </form>

        <div className="flex flex-col items-center gap-3 mt-6">
          <Link to="/pin-login" className="text-sm text-[#338edc] hover:text-[#2b7bc4] font-medium transition-colors">
            {t('mobilePortal.pinLogin.loginWithPin', 'Per PIN anmelden')}
          </Link>
          <p className="text-xs text-[var(--text-tertiary)]">
            {t('auth.noAccount')}
          </p>
        </div>
      </div>
    </div>
  );
}
